'use client';
import useSWR from 'swr';
import { createFetch, IOptions, logError } from './api';
import { MessagePlugin } from 'tdesign-react';

interface IClientOptions extends IOptions {
  isOnlyValidateReq?: boolean;
  cache?: RequestCache;
  errorMessage?: boolean; // 开启提示
  noRedirect?: boolean; // 禁止 401/403 时自动跳转登录
  acceptUnauthorized?: boolean; // 将 401/403 作为已处理结果返回
}

export interface ClientApiResponse<T = unknown> {
  code: number;
  data: T;
  msg?: string;
}

const clientFetch = async <T>(
  url: string,
  {
    errorMessage = true,
    acceptUnauthorized = false,
    ...options
  }: IClientOptions
) => {
  try {
    const res = (await createFetch(url, options)) as Response;
    if (!res.ok && [401, 403].includes(res.status) && acceptUnauthorized) {
      return { code: res.status } as T;
    }
    if (!res.ok && !options?.isOnlyValidateReq && !options?.noRedirect) {
      switch (res.status) {
        case 401:
        case 403:
          window.location.href = `/login?s_url=${encodeURIComponent(
            window.location.href
          )}`;
      }
    }
    if (res.type == 'cors') return res as T;

    // 获取content-disposition响应头
    const contentDisposition = res.headers.get('content-disposition') ?? '';
    if (contentDisposition.includes('attachment')) return { data: res } as T;

    const data = res ? await res?.json() : res;
    // 错误提示
    if (data.code !== 0 && errorMessage) {
      MessagePlugin.error({ zIndex: 1000000, content: data.msg });
      throw new Error(data.msg);
    }
    return data as T;
  } catch (error: any) {
    console.log('error');
    logError('error', url, options.method || 'GET', error);
    throw error;
  }
};

export const ClientApi = {
  async get<T>(url: string, options: IClientOptions = {}) {
    return await clientFetch<T>(url, options);
  },
  async post(url: string, data: any = {}, options: IClientOptions = {}) {
    const res = await clientFetch<{ data: any }>(url, {
      data,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
      },
      ...options,
    });
    return res?.data;
  },
  async postResponse<T>(
    url: string,
    data: unknown = {},
    options: IClientOptions = {}
  ) {
    return await clientFetch<ClientApiResponse<T>>(url, {
      data,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
      },
      ...options,
    });
  },
  async delete(url: string, options: IClientOptions = {}) {
    return await clientFetch(url, { method: 'DELETE', ...options });
  },
  async put<T>(url: string, data: any = {}, options: IClientOptions = {}) {
    const res = await clientFetch(url, {
      data,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
      },
      ...options,
    });
    return res as T;
  },
};

export const useClientGet = (url: string) => {
  return useSWR(url, ClientApi.get);
};

export const useClientDelete = (url: string) => {
  return useSWR(url, ClientApi.delete);
};
